import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaBook, FaChartLine, FaSave, FaTimes, FaTrash, FaUndo } from 'react-icons/fa';
import TextInput from '../../widgets/TextInput';
import NumberInput from '../../widgets/NumberInput';
import SelectInput from '../../widgets/SelectInput';
import ModalDialog from '../../common/ModalDialog/ModalDialog';
import PopupDialog from '../../common/PopupDialog/PopupDialog';
import { FixedAsset } from '../../../models/FixedAsset';
import { SupplierInvoice } from '../../../models/SupplierInvoice';
import { Supplier } from '../../../models/Supplier';
import { DepreciationPeriod } from '../../../models/DepreciationPeriod';
import { Ledger } from '../../../models/Ledger';
import { FiscalYear } from '../../../models/FiscalYear';
import './FixedAssetDialog.scss';

interface FixedAssetDialogProps {
    open: boolean;
    asset: FixedAsset | null;
    supplierInvoices: SupplierInvoice[];
    suppliers: Supplier[];
    depreciationPeriods: DepreciationPeriod[];
    costLedgers: Ledger[];
    fiscalYears: FiscalYear[];
    onSave: (asset: FixedAsset) => Promise<FixedAsset>;
    onDelete: (asset: FixedAsset) => Promise<void>;
    onBook: (asset: FixedAsset, remainderCostLedgerId: string | null) => Promise<void>;
    onUnbook: (asset: FixedAsset) => Promise<void>;
    onBookDepreciation: (asset: FixedAsset, fiscalYearId: string) => Promise<void>;
    onClose: () => void;
}

const empty: FixedAsset = {
    id: '',
    supplierInvoiceId: '',
    depreciationPeriodId: '',
    name: '',
    dateOfPurchase: '',
    price: 0,
    businessUsePercentage: 100,
    remainingValue: 0,
    depreciationPerYear: 0,
    depreciationAmount: 0,
    yearsSincePurchase: 0,
    isCompletelyDepreciated: false,
    isBooked: false,
    bookedAt: null,
};

const FixedAssetDialog: React.FC<FixedAssetDialogProps> = ({
    open, asset, supplierInvoices, suppliers, depreciationPeriods, costLedgers, fiscalYears,
    onSave, onDelete, onBook, onUnbook, onBookDepreciation, onClose,
}) => {
    const { t } = useTranslation(['common', 'screens', 'validation']);
    const [data, setData] = useState<FixedAsset>(asset ?? empty);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showBookConfirm, setShowBookConfirm] = useState(false);
    const [showUnbookConfirm, setShowUnbookConfirm] = useState(false);
    const [showDepreciationConfirm, setShowDepreciationConfirm] = useState(false);
    const [remainderLedgerId, setRemainderLedgerId] = useState<string>('');
    const [depreciationFiscalYearId, setDepreciationFiscalYearId] = useState<string>('');

    useEffect(() => {
        setData(asset ?? { ...empty });
        setValidationError(null);
        setShowDeleteConfirm(false);
        setShowBookConfirm(false);
        setShowUnbookConfirm(false);
        setShowDepreciationConfirm(false);
        setRemainderLedgerId('');
        setDepreciationFiscalYearId('');
    }, [asset, open]);

    // selectedPeriod and availableDepreciationYears must be computed before the early
    // return so that useMemo is always called in the same order (Rules of Hooks).
    const selectedPeriod = depreciationPeriods.find(p => p.id === data.depreciationPeriodId);

    const availableDepreciationYears = useMemo(() => {
        if (!selectedPeriod || !data.dateOfPurchase) return fiscalYears;
        const purchaseDayMs = new Date(data.dateOfPurchase).getTime();
        const depreciationEndMs = purchaseDayMs + selectedPeriod.years * 365.25 * 24 * 60 * 60 * 1000;
        return fiscalYears.filter(fy => {
            const fyEndMs = new Date(fy.end).getTime();
            const fyStartMs = new Date(fy.start).getTime();
            return fyEndMs >= purchaseDayMs && fyStartMs <= depreciationEndMs;
        });
    }, [fiscalYears, selectedPeriod, data.dateOfPurchase]);

    if (!open) return null;

    const isNew = !data.id;
    const set = <K extends keyof FixedAsset>(field: K, value: FixedAsset[K]) =>
        setData(prev => ({ ...prev, [field]: value }));

    const handleInvoiceChange = (invoiceId: string) => {
        setData(prev => {
            const inv = supplierInvoices.find(i => i.id === invoiceId);
            return {
                ...prev,
                supplierInvoiceId: invoiceId,
                dateOfPurchase: inv?.invoiceDate ?? prev.dateOfPurchase,
                price: inv?.totalAmountExVat ?? prev.price,
            };
        });
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setValidationError(null);
        if (!data.name?.trim() || !data.depreciationPeriodId || !data.dateOfPurchase) {
            setValidationError(t('validation:requiredFields'));
            return;
        }
        try {
            const saved = await onSave(data);
            setData(saved);
        } catch {
            // parent handles toast
        }
    };

    const invoiceOptions = supplierInvoices.map(inv => {
        const supplier = suppliers.find(s => s.id === inv.supplierId);
        const label = [supplier?.name, inv.invoiceNumber, inv.invoiceDate].filter(Boolean).join(' — ');
        return { label, value: inv.id };
    });

    const periodOptions = depreciationPeriods.map(p => ({
        label: `${p.name} (${p.years} ${t('screens:fixedAssets.years')})`,
        value: p.id,
    }));

    // Detect a partial-invoice scenario: asset's business-use amount is less than the
    // invoice ex-VAT total, meaning the invoice covers more than just this asset.
    const linkedInvoice = supplierInvoices.find(i => i.id === data.supplierInvoiceId);
    const assetBusinessAmount = data.price * (data.businessUsePercentage / 100);
    const isPartialInvoice = !!(
        linkedInvoice &&
        linkedInvoice.totalAmountExVat > 0 &&
        assetBusinessAmount < linkedInvoice.totalAmountExVat
    );
    const remainderAmount = isPartialInvoice
        ? (linkedInvoice!.totalAmountExVat - assetBusinessAmount).toFixed(2)
        : '0.00';

    const costLedgerOptions = costLedgers.map(l => ({
        label: `${l.code} — ${l.name}`,
        value: l.id,
    }));

    const depreciationYearOptions = availableDepreciationYears.map(fy => ({
        label: `${fy.name} (${fy.start} → ${fy.end})`,
        value: fy.id,
    }));

    return (
        <>
            <ModalDialog
                open={open}
                title={isNew ? t('screens:fixedAssets.dialog.titleNew') : t('screens:fixedAssets.dialog.titleEdit')}
                subtitle={!isNew ? data.name : undefined}
                onClose={onClose}
                onSubmit={handleSave}
                width={720}
                footerLeft={!isNew ? (
                    <div className="fa-dialog__footer-left-actions">
                        {!data.isBooked ? (
                            <button
                                type="button"
                                className="btn btn-info btn-sm me-2"
                                onClick={() => setShowBookConfirm(true)}
                            >
                                <FaBook style={{ marginRight: 5 }} />{t('screens:fixedAssets.dialog.book')}
                            </button>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-outline-warning btn-sm me-2"
                                    onClick={() => setShowUnbookConfirm(true)}
                                >
                                    <FaUndo style={{ marginRight: 5 }} />{t('screens:fixedAssets.dialog.unbook')}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-info btn-sm me-2"
                                    onClick={() => setShowDepreciationConfirm(true)}
                                    disabled={depreciationYearOptions.length === 0}
                                    title={depreciationYearOptions.length === 0 ? t('screens:fixedAssets.dialog.bookDepreciationDisabled') : undefined}
                                >
                                    <FaChartLine style={{ marginRight: 5 }} />{t('screens:fixedAssets.dialog.bookDepreciation')}
                                </button>
                            </>
                        )}
                        <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => setShowDeleteConfirm(true)}
                            disabled={data.isBooked}
                            title={data.isBooked ? t('screens:fixedAssets.dialog.deleteDisabledBooked') : undefined}
                        >
                            <FaTrash style={{ marginRight: 5 }} />{t('screens:fixedAssets.dialog.delete')}
                        </button>
                    </div>
                ) : undefined}
                footerRight={
                    <>
                        <button type="submit" className="btn btn-primary me-2">
                            <FaSave style={{ marginRight: 5 }} />{t('screens:fixedAssets.dialog.save')}
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            <FaTimes style={{ marginRight: 5 }} />{t('screens:fixedAssets.dialog.close')}
                        </button>
                    </>
                }
            >
                <div className="fa-dialog__content">
                    {validationError && <div className="alert alert-danger">{validationError}</div>}

                    {/* ── Asset details ── */}
                    <div className="fa-dialog__section">
                        <div className="fa-dialog__section-title">{t('screens:fixedAssets.dialog.sectionDetails')}</div>
                        <div className="fa-dialog__row">
                            <div className="fa-dialog__col fa-dialog__col--wide">
                                <TextInput
                                    label={t('screens:fixedAssets.columns.name')}
                                    value={data.name}
                                    required
                                    onChange={v => set('name', v)}
                                />
                            </div>
                            <div className="fa-dialog__col">
                                <SelectInput
                                    label={t('screens:fixedAssets.columns.depreciationPeriod')}
                                    value={data.depreciationPeriodId}
                                    options={periodOptions}
                                    required
                                    onChange={v => set('depreciationPeriodId', v)}
                                />
                            </div>
                        </div>
                        <div className="fa-dialog__row">
                            <div className="fa-dialog__col fa-dialog__col--full">
                                <SelectInput
                                    label={t('screens:fixedAssets.columns.supplierInvoice')}
                                    value={data.supplierInvoiceId}
                                    options={invoiceOptions}
                                    onChange={handleInvoiceChange}
                                />
                            </div>
                        </div>
                    </div>

                    {/* ── Purchase info ── */}
                    <div className="fa-dialog__section">
                        <div className="fa-dialog__section-title">{t('screens:fixedAssets.dialog.sectionPurchase')}</div>
                        <div className="fa-dialog__row">
                            <div className="fa-dialog__col">
                                <div className="mb-3">
                                    <label className="form-label">
                                        {t('screens:fixedAssets.columns.dateOfPurchase')}
                                        <span className="text-danger ms-1">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={data.dateOfPurchase ?? ''}
                                        onChange={e => set('dateOfPurchase', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="fa-dialog__col">
                                <NumberInput
                                    label={t('screens:fixedAssets.columns.price')}
                                    value={data.price}
                                    min={0}
                                    decimals={2}
                                    onChange={v => set('price', v)}
                                />
                            </div>
                            <div className="fa-dialog__col">
                                <NumberInput
                                    label={t('screens:fixedAssets.columns.businessUsePercentage')}
                                    value={data.businessUsePercentage}
                                    min={0}
                                    max={100}
                                    decimals={0}
                                    onChange={v => set('businessUsePercentage', v)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* ── Depreciation status (read-only, only visible when editing) ── */}
                    {!isNew && (
                        <div className="fa-dialog__section">
                            <div className="fa-dialog__section-title">{t('screens:fixedAssets.dialog.sectionDepreciation')}</div>
                            <div className="fa-dialog__status-grid">
                                <div className="fa-dialog__status-item">
                                    <span className="fa-dialog__status-label">{t('screens:fixedAssets.columns.yearsSincePurchase')}</span>
                                    <span className="fa-dialog__status-value">{data.yearsSincePurchase}</span>
                                </div>
                                {selectedPeriod && (
                                    <div className="fa-dialog__status-item">
                                        <span className="fa-dialog__status-label">{t('screens:fixedAssets.dialog.totalYears')}</span>
                                        <span className="fa-dialog__status-value">{selectedPeriod.years}</span>
                                    </div>
                                )}
                                <div className="fa-dialog__status-item">
                                    <span className="fa-dialog__status-label">{t('screens:fixedAssets.columns.depreciationPerYear')}</span>
                                    <span className="fa-dialog__status-value">€ {data.depreciationPerYear.toFixed(2)}</span>
                                </div>
                                <div className="fa-dialog__status-item">
                                    <span className="fa-dialog__status-label">{t('screens:fixedAssets.columns.depreciationAmount')}</span>
                                    <span className="fa-dialog__status-value">€ {data.depreciationAmount.toFixed(2)}</span>
                                </div>
                                <div className="fa-dialog__status-item">
                                    <span className="fa-dialog__status-label">{t('screens:fixedAssets.columns.remainingValue')}</span>
                                    <span className="fa-dialog__status-value">€ {data.remainingValue.toFixed(2)}</span>
                                </div>
                                <div className="fa-dialog__status-item">
                                    <span className="fa-dialog__status-label">{t('screens:fixedAssets.columns.status')}</span>
                                    <span className={`badge ${data.isCompletelyDepreciated ? 'bg-secondary' : 'bg-success'}`}>
                                        {data.isCompletelyDepreciated
                                            ? t('screens:fixedAssets.fullyDepreciated')
                                            : t('screens:fixedAssets.active')}
                                    </span>
                                </div>
                                <div className="fa-dialog__status-item">
                                    <span className="fa-dialog__status-label">{t('screens:fixedAssets.dialog.bookingStatus')}</span>
                                    <span className={`badge ${data.isBooked ? 'bg-primary' : 'bg-warning text-dark'}`}>
                                        {data.isBooked
                                            ? t('screens:fixedAssets.dialog.bookedStatus')
                                            : t('screens:fixedAssets.dialog.notBookedStatus')}
                                    </span>
                                </div>
                            </div>
                            <p className="fa-dialog__readonly-note">{t('screens:fixedAssets.dialog.readonlyNote')}</p>
                        </div>
                    )}
                </div>
            </ModalDialog>

            <PopupDialog
                open={showDeleteConfirm}
                title={t('screens:fixedAssets.dialog.deleteTitle')}
                onClose={() => setShowDeleteConfirm(false)}
                actions={
                    <>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowDeleteConfirm(false)}>
                            {t('common:cancel')}
                        </button>
                        <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={async () => { await onDelete(data); setShowDeleteConfirm(false); }}
                        >
                            <FaTrash style={{ marginRight: 5 }} />{t('screens:fixedAssets.dialog.deleteConfirm')}
                        </button>
                    </>
                }
            >
                <p>{t('screens:fixedAssets.dialog.deleteMessage')}</p>
            </PopupDialog>

            <PopupDialog
                open={showBookConfirm}
                title={t('screens:fixedAssets.dialog.bookTitle')}
                width={460}
                onClose={() => { setShowBookConfirm(false); setRemainderLedgerId(''); }}
                actions={
                    <>
                        <button type="button" className="btn btn-secondary btn-sm"
                            onClick={() => { setShowBookConfirm(false); setRemainderLedgerId(''); }}>
                            {t('common:cancel')}
                        </button>
                        <button
                            type="button"
                            className="btn btn-info btn-sm"
                            disabled={isPartialInvoice && !remainderLedgerId}
                            onClick={async () => {
                                await onBook(data, isPartialInvoice ? remainderLedgerId : null);
                                setShowBookConfirm(false);
                                setRemainderLedgerId('');
                            }}
                        >
                            <FaBook style={{ marginRight: 5 }} />{t('screens:fixedAssets.dialog.bookConfirm')}
                        </button>
                    </>
                }
            >
                <p>{t('screens:fixedAssets.dialog.bookMessage')}</p>
                {isPartialInvoice && (
                    <div className="fa-dialog__partial-booking">
                        <div className="fa-dialog__partial-info">
                            <span className="fa-dialog__status-label">
                                {t('screens:fixedAssets.dialog.partialBookingInfo', {
                                    assetAmount: assetBusinessAmount.toFixed(2),
                                    remainderAmount,
                                })}
                            </span>
                        </div>
                        <SelectInput
                            label={t('screens:fixedAssets.dialog.remainderCostLedger')}
                            value={remainderLedgerId}
                            options={costLedgerOptions}
                            required
                            onChange={v => setRemainderLedgerId(v)}
                        />
                    </div>
                )}
            </PopupDialog>

            <PopupDialog
                open={showUnbookConfirm}
                title={t('screens:fixedAssets.dialog.unbookTitle')}
                onClose={() => setShowUnbookConfirm(false)}
                actions={
                    <>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowUnbookConfirm(false)}>
                            {t('common:cancel')}
                        </button>
                        <button
                            type="button"
                            className="btn btn-warning btn-sm"
                            onClick={async () => {
                                await onUnbook(data);
                                setShowUnbookConfirm(false);
                            }}
                        >
                            <FaUndo style={{ marginRight: 5 }} />{t('screens:fixedAssets.dialog.unbookConfirm')}
                        </button>
                    </>
                }
            >
                <p>{t('screens:fixedAssets.dialog.unbookMessage')}</p>
            </PopupDialog>

            <PopupDialog
                open={showDepreciationConfirm}
                title={t('screens:fixedAssets.dialog.bookDepreciationTitle')}
                width={480}
                onClose={() => { setShowDepreciationConfirm(false); setDepreciationFiscalYearId(''); }}
                actions={
                    <>
                        <button type="button" className="btn btn-secondary btn-sm"
                            onClick={() => { setShowDepreciationConfirm(false); setDepreciationFiscalYearId(''); }}>
                            {t('common:cancel')}
                        </button>
                        <button
                            type="button"
                            className="btn btn-info btn-sm"
                            disabled={!depreciationFiscalYearId}
                            onClick={async () => {
                                await onBookDepreciation(data, depreciationFiscalYearId);
                                setShowDepreciationConfirm(false);
                                setDepreciationFiscalYearId('');
                            }}
                        >
                            <FaChartLine style={{ marginRight: 5 }} />{t('screens:fixedAssets.dialog.bookDepreciationConfirm')}
                        </button>
                    </>
                }
            >
                <p>{t('screens:fixedAssets.dialog.bookDepreciationMessage')}</p>
                {selectedPeriod && (
                    <p className="fa-dialog__depreciation-info">
                        {t('screens:fixedAssets.dialog.bookDepreciationAmount', {
                            amount: (data.price * (data.businessUsePercentage / 100) / selectedPeriod.years).toFixed(2),
                        })}
                    </p>
                )}
                <SelectInput
                    label={t('screens:fixedAssets.dialog.bookDepreciationFiscalYear')}
                    value={depreciationFiscalYearId}
                    options={depreciationYearOptions}
                    required
                    onChange={v => setDepreciationFiscalYearId(v)}
                />
            </PopupDialog>
        </>
    );
};

export default FixedAssetDialog;








